// src/utils/dataProcessing.js
import { format, parse, isValid } from 'date-fns';
import { currentConfig } from '../../contexts/ConfigContext';

// Add this helper function to analyze timestamp patterns
const analyzeTimestamps = (data, columnName) => {
  if (!data || data.length < 2) return { showTime: false, displayFormat: 'dd/MM/yyyy' };
  
  const timestamps = data
    .map(row => {
      const value = row[columnName];
      if (!value) return null;
      
      const dateString = typeof value === 'object' && value !== null
        ? (value.value || value.toString())
        : String(value);
      
      try {
        return new Date(dateString);
      } catch (e) {
        return null;
      }
    })
    .filter(date => date && isValid(date));
  
  if (timestamps.length < 2) return { showTime: false, displayFormat: 'dd/MM/yyyy' };
  
  // Check what components vary
  const years = new Set(timestamps.map(d => d.getFullYear()));
  const months = new Set(timestamps.map(d => d.getMonth()));
  const days = new Set(timestamps.map(d => d.getDate()));
  const hours = new Set(timestamps.map(d => d.getHours()));
  const minutes = new Set(timestamps.map(d => d.getMinutes()));
  const seconds = new Set(timestamps.map(d => d.getSeconds()));
  const milliseconds = new Set(timestamps.map(d => d.getMilliseconds()));
  
  // Check if all times are identical (same hour, minute, second)
  const allTimesIdentical = hours.size === 1 && minutes.size === 1 && seconds.size === 1;
  
  // Determine display format based on what varies
  let displayFormat;
  let showTime = false;
  
  if (allTimesIdentical) {
    // Only date varies - all times are the same
    displayFormat = 'dd/MM/yyyy';
  } else if (seconds.size === 1 && seconds.has(0) && milliseconds.size === 1) {
    // Hours and/or minutes vary, but seconds don't
    displayFormat = 'dd/MM/yyyy HH:mm';
    showTime = true;
  } else if (milliseconds.size > 1) {
    // Milliseconds vary
    displayFormat = 'dd/MM/yyyy HH:mm:ss.SSS';
    showTime = true;
  } else {
    // Seconds vary but not milliseconds
    displayFormat = 'dd/MM/yyyy HH:mm:ss';
    showTime = true;
  }
  
  return { showTime, displayFormat };
};

export const formatDate = (dateValue, schemaType) => {
  if (!dateValue) return '';

  const dateString = typeof dateValue === 'object' && dateValue !== null
    ? (dateValue.value || dateValue.toString())
    : String(dateValue);

  const formats = [
    { format: 'yyyy-MM-dd HH:mm:ss', output: 'dd/MM/yyyy HH:mm:ss' },
    { format: 'yyyy-MM-dd', output: 'dd/MM/yyyy' },
    { format: 'yyyy-MM', output: 'MM/yyyy' },
    { format: 'MM-yyyy', output: 'MM/yyyy' },
    { format: 'yyyy', output: 'yyyy' },
    { format: 'MM/yyyy', output: 'MM/yyyy' },
    { format: 'MM/dd/yyyy', output: 'dd/MM/yyyy' },
    { format: 'dd/MM/yyyy', output: 'dd/MM/yyyy' },
    { format: 'yyyy/MM/dd', output: 'dd/MM/yyyy' },
    { format: 'MM yy', output: 'MM/yyyy' }
  ];

  if (schemaType) {
    if (schemaType.toLowerCase().includes('timestamp')) {
      const parsedDate = new Date(dateString);
      return isValid(parsedDate) ? format(parsedDate, 'dd/MM/yyyy HH:mm:ss') : dateString;
    } else if (schemaType.toLowerCase().includes('date')) {
      const parsedDate = parse(dateString, 'yyyy-MM-dd', new Date());
      if (isValid(parsedDate)) {
        return format(parsedDate, 'dd/MM/yyyy');
      }
    }
  }

  for (const { format: formatString, output } of formats) {
    const parsedDate = parse(dateString, formatString, new Date());
    if (isValid(parsedDate)) {
      if (formatString.includes('HH:mm:ss')) {
        return format(parsedDate, 'dd/MM/yyyy HH:mm:ss');
      }
      if (formatString.includes('dd') || formatString.includes('DD')) {
        return format(parsedDate, 'dd/MM/yyyy');
      }
      return format(parsedDate, output);
    }
  }

  return dateString;
};

export const trimDecimals = (value) => {
  if (typeof value === 'number' || (typeof value === 'string' && !isNaN(parseFloat(value)))) {
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return Number(num.toFixed(2));
  }
  return value;
};

export const formatNumber = (value, columnName) => {
  const exemptedColumns = currentConfig?.processing?.exemptedColumns || [];
  
  const isExempted = exemptedColumns.some(exemptCol => {
    // Normalize by replacing underscores with spaces and converting to lowercase
    const normalizedColumnName = columnName.toLowerCase().replace(/_/g, ' ');
    const normalizedExemptCol = exemptCol.toLowerCase().replace(/_/g, ' ');
    return normalizedColumnName === normalizedExemptCol;
  });
  if (isExempted) {
    return value;
  }

  if (typeof value === 'number' || (typeof value === 'string' && !isNaN(parseFloat(value)))) {
    const num = typeof value === 'string' ? parseFloat(value) : value;
    const absNum = Math.abs(num);
    
    if (absNum >= 1e9) {
      return (num / 1e9).toFixed(1) + 'B';
    } else if (absNum >= 1e6) {
      return (num / 1e6).toFixed(1) + 'M';
    } else if (absNum >= 1e3) {
      return (num / 1e3).toFixed(1) + 'k';
    } else {
      return num.toLocaleString('en-US', { 
        minimumFractionDigits: 0, 
        maximumFractionDigits: 2 
      });
    }
  }
  return value;
};

export const formatColumnName = (name) => {
  if (typeof name !== 'string') {
    return String(name);
  }
  return name
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const processData = (rawData, schema, isNumberFormatted) => {
  if (!Array.isArray(rawData)) {
    return [];
  }

  if (!schema || !Array.isArray(schema.fields)) {
    if (rawData.length > 0) {
      schema = {
        fields: Object.keys(rawData[0]).map(key => ({
          name: key,
          type: typeof rawData[0][key] === 'number' ? 'FLOAT' : 'STRING'
        }))
      };
    } else {
      return [];
    }
  }

  // Pre-analyze timestamp columns
  const timestampAnalysis = {};
  schema.fields.forEach(field => {
    if (field.type && (
      field.type.toLowerCase().includes('timestamp') || 
      field.type.toLowerCase().includes('datetime') ||
      field.type.toLowerCase().includes('date')
    )) {
      timestampAnalysis[field.name] = analyzeTimestamps(rawData, field.name);
    }
  });

  return rawData.map(item => {
    if (!item || typeof item !== 'object') {
      return {};
    }

    const processedItem = {};
    Object.entries(item).forEach(([key, value]) => {
      let fieldSchema = schema.fields.find(field => field.name === key);
      
      if (!fieldSchema) {
        const inferredType = (() => {
          if (typeof value === 'number' || !isNaN(parseFloat(value))) return 'FLOAT';
          if (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}/)) return 'DATE';
          return 'STRING';
        })();
        
        fieldSchema = { name: key, type: inferredType };
      }
  
      let displayValue, sortValue;
      
      if (value === null || value === undefined) {
        displayValue = '';
        sortValue = Number.NEGATIVE_INFINITY;
      } else {
        const isTransposedData = 'Field' in item;
        const fieldName = isTransposedData ? item.Field : null;
        const exemptedColumns = currentConfig?.processing?.exemptedColumns || [];
        const shouldExemptField = isTransposedData && exemptedColumns.some(exemptCol => {
          if (!fieldName) return false;
          // Normalize by replacing underscores with spaces and converting to lowercase
          const normalizedFieldName = fieldName.toLowerCase().replace(/_/g, ' ');
          const normalizedExemptCol = exemptCol.toLowerCase().replace(/_/g, ' ');
          return normalizedFieldName === normalizedExemptCol;
        });

        switch (fieldSchema.type) {
          case 'FLOAT':
            case 'INTEGER':
            case 'NUMERIC': {
              const cleanValue = typeof value === 'string' ? 
                value.replace(/[,\s]/g, '') : value;
              
              const numericValue = typeof cleanValue === 'number' ? 
                cleanValue : parseFloat(cleanValue);
  
              // Add explicit logging for debugging
              if (key.includes('payment') || key.includes('price')) {
                console.log(`🔍 Processing ${key}:`, { 
                  original: value, 
                  cleaned: cleanValue, 
                  numeric: numericValue 
                });
              }
  
              if (!isNaN(numericValue)) {
                const trimmedValue = trimDecimals(numericValue);
                const shouldExemptKey = !isTransposedData && exemptedColumns.some(exemptCol => {
                  // Normalize by replacing underscores with spaces and converting to lowercase
                  const normalizedKey = key.toLowerCase().replace(/_/g, ' ');
                  const normalizedExemptCol = exemptCol.toLowerCase().replace(/_/g, ' ');
                  return normalizedKey === normalizedExemptCol;
                });
                if (shouldExemptField || shouldExemptKey) {
                  displayValue = trimmedValue;
                } else {
                  displayValue = isNumberFormatted ? 
                    formatNumber(trimmedValue, key) : 
                    trimmedValue.toLocaleString('en-US', {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 2
                    });
                }
                sortValue = numericValue;
              } else {
                console.warn(`🔍 Failed to parse numeric value for ${key}:`, value);
                displayValue = value;
                sortValue = Number.NEGATIVE_INFINITY;
              }
              break;
            }
          case 'DATE':
          case 'DATETIME':
          case 'TIMESTAMP':
            case 'STRING': {
              // Special handling for dates in STRING type columns
              const stringValue = typeof value === 'object' && value !== null ? 
                (value.value || value.toString()) : String(value);
              
              // Get timestamp analysis for this column
              const analysis = timestampAnalysis[key] || { showTime: false, displayFormat: 'dd/MM/yyyy' };
              
              // Enhanced date pattern detection
              const datePatterns = [
                { regex: /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/, format: 'yyyy-MM-dd\'T\'HH:mm:ss.SSS\'Z\'', display: analysis.displayFormat }, // ISO datetime with milliseconds and Z
                { regex: /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/, format: 'yyyy-MM-dd\'T\'HH:mm:ss', display: analysis.displayFormat }, // ISO datetime
                { regex: /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}/, format: 'yyyy-MM-dd HH:mm:ss', display: analysis.displayFormat }, // SQL datetime
                { regex: /^\d{4}-\d{2}-\d{2}$/, format: 'yyyy-MM-dd', display: 'dd/MM/yyyy' }, // ISO date
                { regex: /^\d{4}-\d{2}$/, format: 'yyyy-MM', display: 'MM/yyyy', appendDay: true }, // Year-month
                { regex: /^\d{2}\/\d{2}\/\d{4}$/, format: 'dd/MM/yyyy', display: 'dd/MM/yyyy' }, // DD/MM/YYYY
                { regex: /^\d{4}\/\d{2}\/\d{2}$/, format: 'yyyy/MM/dd', display: 'dd/MM/yyyy' }, // YYYY/MM/DD
              ];
              
              // Check if the column name suggests it's a date
              const isDateColumnName = /date|month|year|time|period|week|quarter/i.test(key);
              
              // Try to parse as date using various patterns
              let parsed = false;
              for (const pattern of datePatterns) {
                if (pattern.regex.test(stringValue)) {
                  try {
                    // For YYYY-MM format, append -01 to make it a valid date
                    const dateString = pattern.appendDay ? `${stringValue}-01` : stringValue;
                    const date = parse(dateString, pattern.appendDay ? 'yyyy-MM-dd' : pattern.format, new Date());
                    
                    if (isValid(date)) {
                      displayValue = format(date, pattern.display);
                      sortValue = date.getTime(); // Use timestamp for sorting
                      parsed = true;
                      break;
                    }
                  } catch (e) {
                    // Continue to next pattern
                  }
                }
              }
              
              // If no pattern matched but column name suggests date, try parsing anyway
              if (!parsed && isDateColumnName && stringValue) {
                try {
                  const date = new Date(stringValue);
                  if (isValid(date) && !isNaN(date.getTime())) {
                    displayValue = format(date, analysis.displayFormat);
                    sortValue = date.getTime();
                    parsed = true;
                  }
                } catch (e) {
                  // Fall through to default string handling
                }
              }
              
              // Default string handling if not a valid date
              if (!parsed) {
                displayValue = stringValue;
                // For sorting, if it's a numeric string, convert to number for proper sorting
                if (/^\d+$/.test(stringValue)) {
                  sortValue = parseInt(stringValue, 10);
                } else if (/^\d+\.\d+$/.test(stringValue)) {
                  sortValue = parseFloat(stringValue);
                } else {
                  sortValue = stringValue.toLowerCase();
                }
              }
              break;
            }
          default: {
            if (typeof value === 'object' && value !== null && 'value' in value) {
              displayValue = String(value.value);
              sortValue = displayValue.toLowerCase();
            } else {
              displayValue = String(value);
              sortValue = displayValue.toLowerCase();
            }
          }
        }
      }
      
      processedItem[key] = { displayValue, sortValue };
    });
    // Preserve prediction metadata
    if (item._isPrediction !== undefined) {
      processedItem._isPrediction = { 
        displayValue: item._isPrediction, 
        sortValue: item._isPrediction 
      };
    }
    if (item._predictionMethod !== undefined) {
      processedItem._predictionMethod = { 
        displayValue: item._predictionMethod, 
        sortValue: item._predictionMethod 
      };
    }
    if (item._predictionIndex !== undefined) {
      processedItem._predictionIndex = { 
        displayValue: item._predictionIndex, 
        sortValue: item._predictionIndex 
      };
    }
    if (item._confidenceLower !== undefined) {
      processedItem._confidenceLower = { 
        displayValue: item._confidenceLower, 
        sortValue: item._confidenceLower 
      };
    }
    if (item._confidenceUpper !== undefined) {
      processedItem._confidenceUpper = { 
        displayValue: item._confidenceUpper, 
        sortValue: item._confidenceUpper 
      };
    }
    
    return processedItem;
  });
};

export const sortData = (data, sortColumn, sortOrder) => {
  if (!Array.isArray(data) || data.length === 0 || !sortColumn) {
    return data;
  }

  return [...data].sort((a, b) => {
    let aValue = a[sortColumn]?.sortValue;
    let bValue = b[sortColumn]?.sortValue;

    // Handle null/undefined/invalid values
    if (aValue === Number.NEGATIVE_INFINITY && bValue === Number.NEGATIVE_INFINITY) return 0;
    if (aValue === Number.NEGATIVE_INFINITY) return 1;
    if (bValue === Number.NEGATIVE_INFINITY) return -1;

    // For numeric values (including timestamps from dates), use direct comparison
    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
    }

    // If they're not both numbers but could be parsed as numbers, parse and compare
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      // Check for ISO date format first
      const aDate = new Date(aValue);
      const bDate = new Date(bValue);
      if (isValid(aDate) && isValid(bDate)) {
        return sortOrder === 'asc' ? aDate - bDate : bDate - aDate;
      }

      // Try numeric comparison if not dates
      const aNum = parseFloat(aValue.replace(/[,\s]/g, ''));
      const bNum = parseFloat(bValue.replace(/[,\s]/g, ''));
      if (!isNaN(aNum) && !isNaN(bNum)) {
        return sortOrder === 'asc' ? aNum - bNum : bNum - aNum;
      }
    }

    // Default to string comparison
    const aString = String(aValue).toLowerCase();
    const bString = String(bValue).toLowerCase();
    return sortOrder === 'asc' ? 
      aString.localeCompare(bString) : 
      bString.localeCompare(aString);
  });
};

// ADD THIS NEW EXPORT AT THE END OF dataProcessing.js (before the final closing brace)
export const detectColumnType = (columnName, schemaType, sampleValues = []) => {
  // If schema already says it's a date, trust it
  if (schemaType && (schemaType.toLowerCase().includes('date') || 
      schemaType.toLowerCase().includes('timestamp'))) {
    return 'DATE';
  }
  
  // For STRING columns, do our smart detection
  if (schemaType === 'STRING') {
    // Check column name
    const isDateColumnName = /date|month|year|time|period|week|quarter/i.test(columnName);
    
    // Check sample values if provided
    if (sampleValues.length > 0) {
      const datePatterns = [
        /^\d{4}-\d{2}$/,          // YYYY-MM
        /^\d{4}-\d{2}-\d{2}$/,    // YYYY-MM-DD
        /^\d{2}\/\d{2}\/\d{4}$/,  // DD/MM/YYYY
        /^\d{4}\/\d{2}\/\d{2}$/,  // YYYY/MM/DD
      ];
      
      const matchCount = sampleValues.filter(val => 
        val && datePatterns.some(pattern => pattern.test(String(val)))
      ).length;
      
      if (matchCount > sampleValues.length * 0.8) { // 80% threshold
        return 'DATE';
      }
    }
    
    // If column name suggests date, treat as date
    if (isDateColumnName) {
      return 'DATE';
    }
  }
  
  // Check for numeric types
  if (['INTEGER', 'FLOAT', 'NUMERIC'].includes((schemaType || '').toUpperCase())) {
    return 'NUMERIC';
  }
  
  return 'STRING';
};